import { withAuth } from "./auth0-service";

const API_URL = import.meta.env.VITE_API_BASE_URL || "https://localhost:7122/api";

export const agentApi = {
  getAll: async () => {
    const options = await withAuth();
    const response = await fetch(`${API_URL}/agent`, options);
    if (!response.ok) {
      const error = await response.text();
      throw new Error(error || "Failed to fetch agents");
    }
    return response.json();
  },

  getById: async (id) => {
    const options = await withAuth();
    const response = await fetch(`${API_URL}/agent/${id}`, options);
    if (!response.ok) {
      const error = await response.text();
      throw new Error(error || "Failed to fetch agent");
    }
    return response.json();
  },

  create: async (data) => {
    const options = await withAuth({
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(data),
    });
    const response = await fetch(`${API_URL}/agent`, options);
    if (!response.ok) {
      const error = await response.text();
      throw new Error(error || "Failed to create agent");
    }
    return response.json();
  },

  update: async (id, data) => {
    const options = await withAuth({
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(data),
    });
    const response = await fetch(`${API_URL}/agent/${id}`, options);
    if (!response.ok) {
      const error = await response.text();
      throw new Error(error || "Failed to update agent");
    }
    return response.json();
  },

  delete: async (id) => {
    const options = await withAuth({
      method: "DELETE",
    });
    const response = await fetch(`${API_URL}/agent/${id}`, options);
    if (!response.ok) {
      const error = await response.text();
      throw new Error(error || "Failed to delete agent");
    }
    return true;
  },

  generateSystemInstructions: async (payload) => {
    try {
      const options = await withAuth({
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      const endpoint = `${API_URL}/agent/system-instructions`;

      const response = await fetch(endpoint, options);

      if (!response.ok) {
        const text = await response.text();
        console.error("Error response from API:", text);
        throw new Error(text || "Failed to generate system instructions");
      }

      const data = await response.json();

      return data; // Return the full response object
    } catch (error) {
      console.error("Error generating instructions:", error);
      throw error;
    }
  },

  playground: async (text, files, agentId) => {
    try {
      const formData = new FormData();

      if (text?.trim()) {
        formData.append("text", text);
      }

      if (files?.length > 0) {
        files.forEach((file) => formData.append("files", file));
      }

      const options = await withAuth({
        method: "POST",
        body: formData,
      });
      const response = await fetch(
        `${API_URL}/agent/playground/${agentId}`,
        options
      );
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(errorText || "Failed to get response from agent");
      }
      return await response.json();
    } catch (error) {
      console.error("Error in agent conversation:", error);
      throw error;
    }
  },
  generateSystemInstructionsFromWizard: async (wizardResponses) => {
    try {
      const payload = {
        instructions: wizardResponses,
      };

      const options = await withAuth({
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      const endpoint = `${API_URL}/agent/system-instructions-wizard`;

      const response = await fetch(endpoint, options);

      if (!response.ok) {
        const text = await response.text();
        console.error("Error response from wizard API:", text);
        throw new Error(
          text || "Failed to generate system instructions from wizard"
        );
      }

      const data = await response.json();

      return data;
    } catch (error) {
      console.error("Error generating wizard instructions:", error);
      throw error;
    }
  },

  deleteAgentContext: async (agentId) => {
    if (!agentId) {
      throw new Error("Agent ID is required to delete context");
    }

    const options = await withAuth({
      method: "DELETE",
    });

    const endpoint = `${API_URL}/agent/delete-agent-context?agentId=${encodeURIComponent(
      agentId
    )}`;
    const response = await fetch(endpoint, options);

    if (!response.ok) {
      const text = await response.text();
      throw new Error(text || "Failed to delete agent context");
    }

    try {
      const data = await response.json();
      return data?.result ?? true;
    } catch (_error) {
      return true;
    }
  },
};

export default agentApi;
